import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigation } from '../../../shared/contexts/NavigationContext';
import styles from './ImageViewer.module.css';

export interface ImageViewerAction {
  icon: string;
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'danger' | 'default';
}

interface ImageViewerProps {
  images: string[];
  initialIndex: number;
  onClose: () => void;
  sourceElement?: HTMLElement | null;
  actions?: ImageViewerAction[];
  showActions?: boolean;
  onIndexChange?: (index: number) => void;
  siteId?: number | null; // ID объекта для отображения участников
  onShowParticipants?: () => void; // Callback для открытия боттом-шита участников
  imagesThumb?: string[]; // Миниатюры (avatar) для progressive loading — показываются размытыми пока грузится full
}

function getImagesSignature(images: string[], imagesThumb?: string[]): string {
  const thumbPart = imagesThumb?.join('\0') ?? '';
  return `${images.join('\0')}\x1e${thumbPart}`;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ images, initialIndex, onClose, sourceElement, actions = [], showActions = false, onIndexChange, siteId, onShowParticipants, imagesThumb }) => {
  const imagesSignature = getImagesSignature(images, imagesThumb);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchEndY, setTouchEndY] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeOffsetY, setSwipeOffsetY] = useState(0);
  const [isVerticalSwipe, setIsVerticalSwipe] = useState(false);
  // Если есть sourceElement, начинаем с анимации, иначе без нее
  const [isAnimating, setIsAnimating] = useState(!!sourceElement);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [shouldTransitionBackground, setShouldTransitionBackground] = useState(false);
  const [showSliderContainer, setShowSliderContainer] = useState(!sourceElement); // Скрываем контейнер при анимации
  const [isClosing, setIsClosing] = useState(false); // Состояние для анимации закрытия
  
  // Состояния для pinch-to-zoom
  const [imageScales, setImageScales] = useState<Map<number, number>>(new Map()); // Масштаб для каждого изображения
  const [imageTranslates, setImageTranslates] = useState<Map<number, { x: number; y: number }>>(new Map()); // Позиция для каждого изображения
  const [isZooming, setIsZooming] = useState(false); // Флаг активного масштабирования
  const [lastPinchDistance, setLastPinchDistance] = useState<number | null>(null); // Последнее расстояние между пальцами
  const [pinchCenter, setPinchCenter] = useState<{ x: number; y: number } | null>(null); // Центр pinch жеста
  const [initialScale, setInitialScale] = useState<number | null>(null); // Начальный масштаб при начале pinch
  const [initialTranslate, setInitialTranslate] = useState<{ x: number; y: number } | null>(null); // Начальная позиция при начале pinch
  const [lastTapTime, setLastTapTime] = useState<number>(0); // Для обработки двойного тапа
  const [isPanning, setIsPanning] = useState(false); // Флаг активного pan (перемещения одним пальцем)
  const [panStartTranslate, setPanStartTranslate] = useState<{ x: number; y: number } | null>(null); // Начальная позиция при начале pan
  const [panStartScale, setPanStartScale] = useState<number | null>(null); // Масштаб при начале pan
  const [panImagePoint, setPanImagePoint] = useState<{ x: number; y: number } | null>(null); // Точка касания в координатах изображения
  const [panContainerCenter, setPanContainerCenter] = useState<{ x: number; y: number } | null>(null); // Центр контейнера при начале pan
  const [needsBoundaryCorrection, setNeedsBoundaryCorrection] = useState<{ x: number; y: number } | null>(null); // Позиция для возврата к границам
  const [fullLoadedIndices, setFullLoadedIndices] = useState<Set<number>>(new Set()); // Progressive loading: какие full изображения загружены
  
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
  const imageWrapperRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Сохраняем базовые размеры изображений (без transform) для точного вычисления границ
  const baseImageSizes = useRef<Map<number, { width: number; height: number }>>(new Map());
  const { pushBackLayer, popBackLayer } = useNavigation();
  const onCloseRef = useRef(onClose);
  const onIndexChangeRef = useRef(onIndexChange);
  const lastNotifiedIndexRef = useRef(initialIndex);

  // Обновляем ref при изменении onClose
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    onIndexChangeRef.current = onIndexChange;
  }, [onIndexChange]);

  // Сбрасываем флаги progressive loading только при реальной смене набора изображений
  useEffect(() => {
    setFullLoadedIndices(new Set());
  }, [imagesSignature]);

  // Проверяем complete для progressive loading (кэш — onLoad может не сработать)
  useEffect(() => {
    if (!imagesThumb) return;
    const checkComplete = () => {
      setFullLoadedIndices(prev => {
        let changed = false;
        const next = new Set(Array.from(prev));
        images.forEach((_, index) => {
          if (!imagesThumb![index] || imagesThumb![index] === images[index]) return;
          const img = imageRefs.current[index];
          if (img?.complete && img.naturalWidth > 0 && !next.has(index)) {
            next.add(index);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };
    const id = requestAnimationFrame(checkComplete);
    return () => cancelAnimationFrame(id);
  }, [imagesSignature, images, imagesThumb]);

  // Вешаем закрытие слайдера поверх текущего слоя «назад» (pushBackLayer), при размонтировании снимаем (popBackLayer), чтобы не терять стрелку ленты/профиля/окна объекта
  useEffect(() => {
    const handleClose = () => onCloseRef.current();
    pushBackLayer(handleClose, true);
    return () => popBackLayer();
  }, [pushBackLayer, popBackLayer]);

  // Минимальная дистанция для свайпа (50px)
  const minSwipeDistance = 50;
  
  // Минимальный и максимальный масштаб
  const MIN_SCALE = 1;
  const MAX_SCALE = 5;
  
  // Вычисление расстояния между двумя точками
  const getDistance = (touch1: React.Touch, touch2: React.Touch): number => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };
  
  // Вычисление центра между двумя точками
  const getCenter = (touch1: React.Touch, touch2: React.Touch): { x: number; y: number } => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };
  
  // Получение текущего масштаба изображения
  const getImageScale = (index: number): number => {
    return imageScales.get(index) || 1;
  };
  
  // Получение текущей позиции изображения
  const getImageTranslate = (index: number): { x: number; y: number } => {
    return imageTranslates.get(index) || { x: 0, y: 0 };
  };
  
  // Сброс масштаба для изображения
  const resetImageTransform = (index: number) => {
    setImageScales(prev => {
      const newMap = new Map(prev);
      newMap.delete(index);
      return newMap;
    });
    setImageTranslates(prev => {
      const newMap = new Map(prev);
      newMap.delete(index);
      return newMap;
    });
  };
  
  // Сохранение базовых размеров для текущего изображения
  useEffect(() => {
    const image = imageRefs.current[currentIndex];
    const imageWrapper = imageWrapperRefs.current[currentIndex];
    
    if (image && imageWrapper && imageContainerRef.current) {
      // Используем requestAnimationFrame для гарантии, что DOM обновлен
      requestAnimationFrame(() => {
        saveBaseImageSize(currentIndex, image, imageWrapper);
      });
    }
  }, [currentIndex, imagesSignature, images.length]);
  
  // Сохранение базовых размеров изображения (когда scale = 1, translate = 0)
  const saveBaseImageSize = (index: number, image: HTMLImageElement, imageWrapper: HTMLDivElement) => {
    if (!imageContainerRef.current) return;
    
    // Получаем размеры контейнера
    const containerRect = imageContainerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    
    // Получаем натуральные размеры изображения
    let naturalWidth = image.naturalWidth;
    let naturalHeight = image.naturalHeight;
    
    // Если натуральные размеры не загружены, используем размеры контейнера
    if (!naturalWidth || !naturalHeight || naturalWidth === 0 || naturalHeight === 0) {
      // Пытаемся получить из отображаемых размеров
      const imageRect = image.getBoundingClientRect();
      if (imageRect.width > 0 && imageRect.height > 0) {
        naturalWidth = imageRect.width;
        naturalHeight = imageRect.height;
      } else {
        naturalWidth = containerWidth;
        naturalHeight = containerHeight;
      }
    }
    
    // Вычисляем размеры изображения с учетом object-fit: contain
    const imageAspectRatio = naturalWidth / naturalHeight;
    const containerAspectRatio = containerWidth / containerHeight;
    
    let baseWidth: number;
    let baseHeight: number;
    
    if (imageAspectRatio > containerAspectRatio) {
      // Изображение шире контейнера - ограничено по ширине
      baseWidth = containerWidth;
      baseHeight = containerWidth / imageAspectRatio;
    } else {
      // Изображение выше контейнера - ограничено по высоте
      baseWidth = containerHeight * imageAspectRatio;
      baseHeight = containerHeight;
    }
    
    // Сохраняем базовые размеры с высокой точностью
    if (baseWidth > 0 && baseHeight > 0) {
      baseImageSizes.current.set(index, { 
        width: Math.round(baseWidth * 100) / 100, 
        height: Math.round(baseHeight * 100) / 100 
      });
    }
  };
  
  // Простое и строгое вычисление границ
  const calculateImageBounds = (index: number, container: HTMLDivElement, scale: number): { maxTranslateX: number; maxTranslateY: number } => {
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    
    // Получаем базовые размеры изображения (без transform)
    const baseSize = baseImageSizes.current.get(index);
    
    if (!baseSize || baseSize.width === 0 || baseSize.height === 0) {
      // Если базовые размеры не сохранены, возвращаем нулевые границы
      return { maxTranslateX: 0, maxTranslateY: 0 };
    }
    
    // Применяем масштаб к базовым размерам
    const scaledWidth = baseSize.width * scale;
    const scaledHeight = baseSize.height * scale;
    
    // Вычисляем максимальные смещения
    // Максимальное смещение = половина разницы между масштабированным размером и размером контейнера
    // Используем более точное вычисление без округления для точности
    const maxTranslateX = scaledWidth > containerWidth 
      ? (scaledWidth - containerWidth) / 2
      : 0;
    const maxTranslateY = scaledHeight > containerHeight 
      ? (scaledHeight - containerHeight) / 2
      : 0;
    
    return { maxTranslateX, maxTranslateY };
  };
  
  // Строгая проверка и корректировка границ
  const clampTranslate = (index: number, translate: { x: number; y: number }, scale: number): { x: number; y: number } => {
    if (!imageContainerRef.current) return translate;
    
    const { maxTranslateX, maxTranslateY } = calculateImageBounds(index, imageContainerRef.current, scale);
    
    // Строго ограничиваем - не позволяем выходить за границы даже на долю пикселя
    // Используем более строгое ограничение для предотвращения выхода за границы
    const clampedX = maxTranslateX > 0
      ? Math.max(-maxTranslateX, Math.min(maxTranslateX, Math.round(translate.x * 100) / 100))
      : 0;
    const clampedY = maxTranslateY > 0
      ? Math.max(-maxTranslateY, Math.min(maxTranslateY, Math.round(translate.y * 100) / 100))
      : 0;
    
    return { x: clampedX, y: clampedY };
  };
  
  // Применяем эффект "резиновой ленты" при выходе за границы
  const applyRubberBand = (index: number, translate: { x: number; y: number }, scale: number): { x: number; y: number } => {
    if (!imageContainerRef.current) return translate;
    
    const { maxTranslateX, maxTranslateY } = calculateImageBounds(index, imageContainerRef.current, scale);
    
    const rubberBand = (value: number, max: number): number => {
      if (max === 0) return 0;
      
      if (Math.abs(value) <= max) {
        return value;
      }
      
      // Вышли за границы - применяем сопротивление
      const overflow = Math.abs(value) - max;
      const sign = value > 0 ? 1 : -1;
      
      // Формула резиновой ленты: чем дальше за границу, тем сильнее сопротивление
      // Максимальный выход за границы - 150px с плавным замедлением
      const resistance = 0.25; // Коэффициент сопротивления (0.25 = 25% от движения)
      const maxOverflow = 150; // Максимальный выход за границы
      const dampenedOverflow = maxOverflow * (1 - Math.exp(-overflow * resistance / maxOverflow));
      
      return sign * (max + dampenedOverflow);
    };
    
    return {
      x: rubberBand(translate.x, maxTranslateX),
      y: rubberBand(translate.y, maxTranslateY)
    };
  };

  // Сбрасываем состояния только при изменении sourceElement (открытие/закрытие)
  // НЕ сбрасываем при изменении initialIndex, чтобы не запускать анимацию открытия заново
  useEffect(() => {
    // Сбрасываем анимацию только если sourceElement изменился (открыли/закрыли слайдер)
    if (sourceElement) {
      // Если слайдер только открылся, запускаем анимацию открытия
      setHasAnimated(false);
      setIsAnimating(true);
      setShouldTransitionBackground(false);
      setShowSliderContainer(false);
    } else {
      // Если sourceElement убран, значит слайдер закрыт - сбрасываем все
      setHasAnimated(false);
      setIsAnimating(false);
      setShouldTransitionBackground(false);
      setShowSliderContainer(true);
    }
    setSwipeOffset(0);
    setSwipeOffsetY(0);
    setIsVerticalSwipe(false);
    setIsPanning(false);
    setPanStartTranslate(null);
    setPanStartScale(null);
    setPanImagePoint(null);
    setPanContainerCenter(null);
  }, [sourceElement]);

  // Синхронизируем индекс с родителем и не выходим за границы массива (напр. после удаления)
  useLayoutEffect(() => {
    if (images.length === 0) return;
    const safeIndex = Math.min(Math.max(initialIndex, 0), images.length - 1);
    setCurrentIndex(prev => (prev === safeIndex ? prev : safeIndex));
  }, [initialIndex, images.length]);

  // Сбрасываем масштаб при переключении изображений
  useEffect(() => {
    // Сбрасываем масштаб для всех изображений кроме текущего
    images.forEach((_, index) => {
      if (index !== currentIndex) {
        resetImageTransform(index);
      }
    });
    // Сбрасываем pan при переключении
    setIsPanning(false);
    setPanStartTranslate(null);
    setPanStartScale(null);
    setPanImagePoint(null);
    setPanContainerCenter(null);
    setNeedsBoundaryCorrection(null);
  }, [currentIndex, images.length]);
  
  // Эффект для возврата к границам после завершения pan
  useEffect(() => {
    if (needsBoundaryCorrection && !isPanning) {
      // Используем requestAnimationFrame для правильного порядка обновления
      // Сначала один кадр для сброса isPanning, затем обновление позиции
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setImageTranslates(prev => {
            const newMap = new Map(prev);
            newMap.set(currentIndex, needsBoundaryCorrection);
            return newMap;
          });
          setNeedsBoundaryCorrection(null);
        });
      });
    }
  }, [needsBoundaryCorrection, isPanning, currentIndex]);

  // Сообщаем родителю только при реальной смене индекса внутри слайдера
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastNotifiedIndexRef.current = currentIndex;
      return;
    }
    if (lastNotifiedIndexRef.current === currentIndex) return;
    lastNotifiedIndexRef.current = currentIndex;
    onIndexChangeRef.current?.(currentIndex);
  }, [currentIndex]);

  useLayoutEffect(() => {
    // Анимация открытия - только при первом открытии слайдера (когда sourceElement появляется)
    // НЕ запускаем при изменении currentIndex - только при открытии
    if (sourceElement && !hasAnimated && isAnimating) {
      const startAnimation = () => {
        const sourceRect = sourceElement.getBoundingClientRect();
      
        // Создаем временный клон изображения для анимации (thumb грузится быстрее)
        const tempImage = document.createElement('img');
        tempImage.src = (imagesThumb?.[initialIndex] && imagesThumb[initialIndex] !== images[initialIndex]) ? imagesThumb[initialIndex] : images[initialIndex];
        tempImage.style.position = 'fixed';
        tempImage.style.left = `${sourceRect.left + sourceRect.width / 2}px`;
        tempImage.style.top = `${sourceRect.top + sourceRect.height / 2}px`;
        tempImage.style.width = `${sourceRect.width}px`;
        tempImage.style.height = `${sourceRect.height}px`;
        tempImage.style.transform = 'translate(-50%, -50%)';
        tempImage.style.objectFit = 'cover';
        tempImage.style.borderRadius = '8px';
        tempImage.style.zIndex = '100001';
        tempImage.style.margin = '0';
        tempImage.style.padding = '0';
        tempImage.style.transition = 'none';
        tempImage.style.opacity = '1';
        tempImage.style.pointerEvents = 'none';
        
        // Добавляем временное изображение в body
        document.body.appendChild(tempImage);
        
        // Вычисляем конечную позицию (центр экрана)
        const finalLeft = window.innerWidth / 2;
        const finalTop = window.innerHeight / 2;
        
        // Вычисляем конечный размер (максимальный с сохранением пропорций)
        const maxWidth = window.innerWidth;
        const maxHeight = window.innerHeight;
        
        // Функция для вычисления финальных размеров
        const calculateFinalSize = () => {
          let imageAspectRatio = sourceRect.width / sourceRect.height;
          
          // Если изображение загружено, используем его натуральные пропорции
          if (tempImage.naturalWidth && tempImage.naturalHeight) {
            imageAspectRatio = tempImage.naturalWidth / tempImage.naturalHeight;
          }
          
          let finalWidth = maxWidth;
          let finalHeight = maxWidth / imageAspectRatio;
          
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = maxHeight * imageAspectRatio;
          }
          
          return { finalWidth, finalHeight };
        };
        
        // Запускаем анимацию когда изображение загружено
        const runAnimation = () => {
          const { finalWidth, finalHeight } = calculateFinalSize();
          
          // Запускаем анимацию через небольшой таймаут
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Включаем transition для фона и начинаем затемнение ОДНОВРЕМЕННО с анимацией фото
              setShouldTransitionBackground(true);
              
              // Начинаем затемнять фон сразу (isAnimating -> false запускает transition)
              // Но оставляем временный клон видимым на время анимации
              setTimeout(() => {
                setIsAnimating(false);
              }, 10);
              
              // Одновременно двигаем и увеличиваем
              tempImage.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
              tempImage.style.left = `${finalLeft}px`;
              tempImage.style.top = `${finalTop}px`;
              tempImage.style.width = `${finalWidth}px`;
              tempImage.style.height = `${finalHeight}px`;
              tempImage.style.objectFit = 'contain';
              tempImage.style.borderRadius = '0';
              
              setTimeout(() => {
                // Показываем контейнер слайдера и плавно скрываем временный клон одновременно
                setShowSliderContainer(true);
                tempImage.style.opacity = '0';
                tempImage.style.transition = 'opacity 0.15s ease';
                setHasAnimated(true);
                
                // Удаляем временное изображение после затухания
                setTimeout(() => {
                  if (tempImage.parentNode) {
                    tempImage.parentNode.removeChild(tempImage);
                  }
                }, 150);
              }, 300);
            });
          });
        };
        
        if (tempImage.complete) {
          runAnimation();
        } else {
          tempImage.onload = runAnimation;
        }
      };
      
      startAnimation();
    }
  }, [sourceElement, hasAnimated, isAnimating, images, imagesThumb, initialIndex]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning || isZooming) return;
    setIsTransitioning(true);
    setSwipeOffset(0);
    setSwipeOffsetY(0);
    // Сбрасываем масштаб перед переключением
    resetImageTransform(currentIndex);
    setCurrentIndex((prev) => prev > 0 ? prev - 1 : images.length - 1);
    setTimeout(() => setIsTransitioning(false), 300);
  }, [isTransitioning, isZooming, images.length, currentIndex]);

  const handleNext = useCallback(() => {
    if (isTransitioning || isZooming) return;
    setIsTransitioning(true);
    setSwipeOffset(0);
    setSwipeOffsetY(0);
    // Сбрасываем масштаб перед переключением
    resetImageTransform(currentIndex);
    setCurrentIndex((prev) => prev < images.length - 1 ? prev + 1 : 0);
    setTimeout(() => setIsTransitioning(false), 300);
  }, [isTransitioning, isZooming, images.length, currentIndex]);

  const handleCloseWithAnimation = useCallback(() => {
    if (isClosing) return; // Предотвращаем множественные вызовы
    
    setIsClosing(true);
    
    // Сбрасываем масштаб всех изображений перед закрытием
    images.forEach((_, index) => {
      resetImageTransform(index);
    });
    
    // Получаем текущее изображение
    const currentImage = imageRefs.current[currentIndex];
    if (!currentImage || !imageContainerRef.current) {
      // Если нет изображения, закрываем сразу
      onClose();
      return;
    }

    const imageRect = currentImage.getBoundingClientRect();
    
    // Используем текущее смещение для более плавной анимации
    const startOpacity = swipeOffsetY < 0 ? Math.max(0, 1 - Math.abs(swipeOffsetY) / 300) : 1;
    const startScale = swipeOffsetY < 0 ? Math.max(0.8, 1 - Math.abs(swipeOffsetY) / 800) : 1;
    
    // Центр экрана для анимации
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    
    // Если есть свайп вверх, используем текущую позицию, иначе центр экрана
    const startLeft = swipeOffsetY < 0 ? imageRect.left + imageRect.width / 2 : centerX;
    const startTop = swipeOffsetY < 0 ? imageRect.top + imageRect.height / 2 + swipeOffsetY : centerY;
    
    // Создаем клон изображения для анимации
    const tempImage = document.createElement('img');
    tempImage.src = images[currentIndex];
    tempImage.style.position = 'fixed';
    tempImage.style.left = `${startLeft}px`;
    tempImage.style.top = `${startTop}px`;
    tempImage.style.width = `${imageRect.width}px`;
    tempImage.style.height = `${imageRect.height}px`;
    tempImage.style.objectFit = 'contain';
    tempImage.style.zIndex = '100001';
    tempImage.style.margin = '0';
    tempImage.style.padding = '0';
    tempImage.style.transition = 'none';
    tempImage.style.opacity = `${startOpacity}`;
    tempImage.style.pointerEvents = 'none';
    tempImage.style.borderRadius = '0';
    tempImage.style.transform = `translate(-50%, -50%) scale(${startScale})`;
    
    // Добавляем временное изображение в body
    document.body.appendChild(tempImage);
    
    // Скрываем оригинальное изображение в слайдере
    setShowSliderContainer(false);
    
    // Запускаем анимацию
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Уменьшаем прозрачность фона
        setShouldTransitionBackground(true);
        
        // Анимируем уменьшение масштаба и прозрачности (остаемся на месте)
        tempImage.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        tempImage.style.opacity = '0';
        tempImage.style.transform = 'translate(-50%, -50%) scale(0)';
        
        // Ждем завершения анимации, затем закрываем
        setTimeout(() => {
          if (tempImage.parentNode) {
            tempImage.parentNode.removeChild(tempImage);
          }
          onClose();
        }, 300);
      });
    });
  }, [isClosing, currentIndex, swipeOffsetY, images, onClose]);

  useEffect(() => {
    // Сохраняем текущую позицию скролла
    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    
    // Блокируем скролл body при открытом просмотре - более агрессивный подход
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalWidth = document.body.style.width;
    const originalHeight = document.body.style.height;
    
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    // Также блокируем скролл на html элементе
    const html = document.documentElement;
    const originalHtmlOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    
    // Прокручиваем к началу страницы при открытии
    window.scrollTo(0, 0);
    
    // Закрытие по Escape
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseWithAnimation();
      }
    };

    // Навигация стрелками
    const handleArrowKeys = (e: KeyboardEvent) => {
      if (isTransitioning) return;
      
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    // Предотвращаем скролл колесом мыши
    const preventScroll = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Предотвращаем скролл на touch устройствах
    const preventTouchMove = (e: TouchEvent) => {
      // Разрешаем только свайпы внутри модального окна
      const target = e.target as HTMLElement;
      if (!target.closest('.imageViewer')) {
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleEscape);
    window.addEventListener('keydown', handleArrowKeys);
    window.addEventListener('wheel', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventTouchMove, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('keydown', handleArrowKeys);
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventTouchMove);
      
      // Восстанавливаем стили body
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;
      document.body.style.height = originalHeight;
      
      // Восстанавливаем стили html
      html.style.overflow = originalHtmlOverflow;
      
      // Восстанавливаем позицию скролла
      window.scrollTo(0, scrollY);
    };
  }, [images.length, isTransitioning, handleCloseWithAnimation, handlePrevious, handleNext]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Закрываем только если кликнули на сам overlay (фон), а не на его содержимое
    if (e.target === e.currentTarget) {
      handleCloseWithAnimation();
    }
  };

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Проверяем что клик был именно на изображении
    const target = e.target as HTMLElement;
    
    if (target.tagName !== 'IMG') {
      // Если клик НЕ на изображении - закрываем окно с анимацией
      // (клик на пустое пространство вокруг изображения)
      handleCloseWithAnimation();
      return;
    }
    
    // Проверяем двойной клик для увеличения/уменьшения масштаба
    const currentTime = Date.now();
    const timeDiff = currentTime - lastTapTime;
    const currentScale = getImageScale(currentIndex);
    
    if (timeDiff < 300 && timeDiff > 0) {
      // Двойной клик
      if (currentScale > MIN_SCALE) {
        // Если увеличено - уменьшаем
        resetImageTransform(currentIndex);
        setLastTapTime(0);
        return;
      } else {
        // Если не увеличено - увеличиваем до 2x
        const targetScale = 2;
        const image = imageRefs.current[currentIndex];
        if (image && imageContainerRef.current) {
          const containerRect = imageContainerRef.current.getBoundingClientRect();
          const clickX = e.clientX - containerRect.left - containerRect.width / 2;
          const clickY = e.clientY - containerRect.top - containerRect.height / 2;
          
          // Вычисляем начальную позицию для центрирования на точке клика
          const initialTranslate = {
            x: -clickX * (targetScale - 1),
            y: -clickY * (targetScale - 1)
          };
          
          setImageScales(prev => {
            const newMap = new Map(prev);
            newMap.set(currentIndex, targetScale);
            return newMap;
          });
          setImageTranslates(prev => {
            const newMap = new Map(prev);
            newMap.set(currentIndex, initialTranslate);
            return newMap;
          });
        }
        setLastTapTime(0);
        return;
      }
    }
    
    setLastTapTime(currentTime);
    
    // Если изображение увеличено, не обрабатываем навигацию
    if (currentScale > MIN_SCALE) {
      return;
    }
    
    // Если клик на изображении и их несколько - навигация
    if (images.length <= 1) return;
    
    if (!imageContainerRef.current) return;
    const rect = imageContainerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    
    // Левая 40% - предыдущее фото
    if (clickX < width * 0.4) {
      handlePrevious();
    } else if (clickX > width * 0.6) {
      // Правая 40% - следующее фото
      handleNext();
    }
    // Центральная 20% - ничего не делаем
  };

  const onTouchStart = (e: React.TouchEvent) => {
    // Если два пальца - начинаем pinch-to-zoom
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getDistance(touch1, touch2);
      const center = getCenter(touch1, touch2);
      
      setLastPinchDistance(distance);
      setPinchCenter(center);
      setIsZooming(true);
      
      // Сохраняем текущий масштаб и позицию
      const currentScale = getImageScale(currentIndex);
      const currentTranslate = getImageTranslate(currentIndex);
      setInitialScale(currentScale);
      setInitialTranslate(currentTranslate);
      
      // Отменяем свайп при начале zoom
      setSwipeOffset(0);
      setSwipeOffsetY(0);
      setIsVerticalSwipe(false);
      return;
    }
    
    // Один палец - проверяем, увеличено ли изображение
    const currentScale = getImageScale(currentIndex);
    const currentTranslate = getImageTranslate(currentIndex);
    
    if (currentScale > MIN_SCALE) {
      // Если изображение увеличено - начинаем pan (перемещение)
      const touchX = e.targetTouches[0].clientX;
      const touchY = e.targetTouches[0].clientY;
      
      setIsPanning(true);
      setPanStartTranslate(currentTranslate);
      setPanStartScale(currentScale); // Сохраняем начальный масштаб для нормализации
      setTouchStart(touchX);
      setTouchStartY(touchY);
      setSwipeOffset(0);
      setSwipeOffsetY(0);
      setIsVerticalSwipe(false);
      setIsZooming(false);
      setLastPinchDistance(null);
      setPinchCenter(null);
      setPanImagePoint(null);
      setPanContainerCenter(null);
    } else {
      // Обычный свайп для навигации
      setTouchEnd(null);
      setTouchEndY(null);
      setTouchStart(e.targetTouches[0].clientX);
      setTouchStartY(e.targetTouches[0].clientY);
      setSwipeOffset(0);
      setSwipeOffsetY(0);
      setIsVerticalSwipe(false);
      setIsZooming(false);
      setIsPanning(false);
      setPanStartTranslate(null);
      setLastPinchDistance(null);
      setPinchCenter(null);
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    // Если два пальца - обрабатываем pinch-to-zoom
    if (e.touches.length === 2 && isZooming && lastPinchDistance !== null && pinchCenter && initialScale !== null && initialTranslate) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getDistance(touch1, touch2);
      const center = getCenter(touch1, touch2);
      
      // Вычисляем изменение масштаба
      const scaleChange = distance / lastPinchDistance;
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, initialScale * scaleChange));
      
      // Вычисляем смещение центра pinch жеста
      const centerDeltaX = center.x - pinchCenter.x;
      const centerDeltaY = center.y - pinchCenter.y;
      
      // Применяем новую позицию с учетом смещения центра
      let newTranslate = {
        x: initialTranslate.x + centerDeltaX,
        y: initialTranslate.y + centerDeltaY
      };
      
      // Применяем эффект "резиновой ленты" при pinch-to-zoom
      newTranslate = applyRubberBand(currentIndex, newTranslate, newScale);
      
      // Обновляем состояние
      setImageScales(prev => {
        const newMap = new Map(prev);
        newMap.set(currentIndex, newScale);
        return newMap;
      });
      setImageTranslates(prev => {
        const newMap = new Map(prev);
        newMap.set(currentIndex, newTranslate);
        return newMap;
      });
      
      // Отменяем свайп при zoom
      setSwipeOffset(0);
      setSwipeOffsetY(0);
      return;
    }
    
    // Один палец - проверяем, pan или свайп
    if (touchStart === null || touchStartY === null || isZooming) return;
    
    const currentX = e.targetTouches[0].clientX;
    const currentY = e.targetTouches[0].clientY;
    
    // Если изображение увеличено - обрабатываем pan (перемещение)
    if (isPanning && panStartTranslate && panStartScale) {
      // Вычисляем дельту смещения пальца
      const deltaX = currentX - touchStart;
      const deltaY = currentY - touchStartY;
      
      // Вычисляем новую позицию напрямую без нормализации
      // Пользователь двигает пальцем - изображение двигается на ту же дистанцию
      let newTranslate = {
        x: panStartTranslate.x + deltaX,
        y: panStartTranslate.y + deltaY
      };
      
      // Применяем эффект "резиновой ленты" - позволяем выйти за границы с сопротивлением
      const currentScale = getImageScale(currentIndex);
      newTranslate = applyRubberBand(currentIndex, newTranslate, currentScale);
      
      // Обновляем позицию
      setImageTranslates(prev => {
        const newMap = new Map(prev);
        newMap.set(currentIndex, newTranslate);
        return newMap;
      });
      
      // Отменяем свайп при pan
      setSwipeOffset(0);
      setSwipeOffsetY(0);
      return;
    }
    
    // Обычный свайп для навигации (только если изображение не увеличено)
    setTouchEnd(currentX);
    setTouchEndY(currentY);
    
    // Вычисляем смещения по обеим осям
    const offsetX = currentX - touchStart;
    const offsetY = currentY - touchStartY;
    
    // Определяем, какое направление преобладает
    const absX = Math.abs(offsetX);
    const absY = Math.abs(offsetY);
    
    // Если вертикальное движение преобладает или равно горизонтальному
    if (absY >= absX && absY > 10) {
      setIsVerticalSwipe(true);
      setSwipeOffsetY(offsetY);
      setSwipeOffset(0); // Сбрасываем горизонтальное смещение
    } else if (absX > absY) {
      setIsVerticalSwipe(false);
      setSwipeOffset(offsetX);
      setSwipeOffsetY(0); // Сбрасываем вертикальное смещение
    }
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    // Если был pinch-to-zoom, сохраняем финальное состояние и проверяем границы
    if (isZooming) {
      setIsZooming(false);
      setLastPinchDistance(null);
      setPinchCenter(null);
      setInitialScale(null);
      setInitialTranslate(null);
      
      // Если масштаб вернулся к 1, сбрасываем позицию
      const currentScale = getImageScale(currentIndex);
      if (currentScale <= MIN_SCALE) {
        resetImageTransform(currentIndex);
      } else {
        // Проверяем и корректируем границы после изменения масштаба
        const currentTranslate = getImageTranslate(currentIndex);
        const correctedTranslate = clampTranslate(currentIndex, currentTranslate, currentScale);
        
        // Если позиция изменилась, обновляем её
        const threshold = 0.01;
        if (Math.abs(correctedTranslate.x - currentTranslate.x) > threshold || 
            Math.abs(correctedTranslate.y - currentTranslate.y) > threshold) {
          setImageTranslates(prev => {
            const newMap = new Map(prev);
            newMap.set(currentIndex, correctedTranslate);
            return newMap;
          });
        }
      }
      
      // Сбрасываем touch состояния
      setTouchStart(null);
      setTouchEnd(null);
      setTouchStartY(null);
      setTouchEndY(null);
      setIsVerticalSwipe(false);
      return;
    }
    
    // Если был pan - завершаем его и проверяем границы
    if (isPanning) {
      const currentScale = getImageScale(currentIndex);
      const currentTranslate = getImageTranslate(currentIndex);
      const image = imageRefs.current[currentIndex];
      
      // Проверяем и корректируем позицию, если изображение вышло за границы
      const correctedTranslate = clampTranslate(currentIndex, currentTranslate, currentScale);
      
      // Если позиция изменилась, сохраняем для возврата к границам
      // Используем небольшую погрешность для сравнения (из-за округления)
      const threshold = 0.01;
      if (Math.abs(correctedTranslate.x - currentTranslate.x) > threshold || 
          Math.abs(correctedTranslate.y - currentTranslate.y) > threshold) {
        setNeedsBoundaryCorrection(correctedTranslate);
      }
      
      // Сбрасываем pan состояния
      setIsPanning(false);
      setPanStartTranslate(null);
      setPanStartScale(null);
      setPanImagePoint(null);
      setPanContainerCenter(null);
      setTouchStart(null);
      setTouchEnd(null);
      setTouchStartY(null);
      setTouchEndY(null);
      setIsVerticalSwipe(false);
      return;
    }
    
    // Обработка двойного тапа для увеличения/уменьшения масштаба
    if (e.touches.length === 0 && touchStart !== null && touchEnd !== null) {
      const currentTime = Date.now();
      const timeDiff = currentTime - lastTapTime;
      const currentScale = getImageScale(currentIndex);
      
      // Проверяем, что это был быстрый тап (не свайп)
      const distance = Math.abs(touchStart - (touchEnd || touchStart)) + Math.abs((touchStartY || 0) - (touchEndY || 0));
      
      if (distance < 10 && timeDiff < 300 && timeDiff > 0) {
        if (currentScale > MIN_SCALE) {
          // Если увеличено - уменьшаем
          resetImageTransform(currentIndex);
          setLastTapTime(0);
        } else {
          // Если не увеличено - увеличиваем до 2x
          const targetScale = 2;
          const image = imageRefs.current[currentIndex];
          if (image && imageContainerRef.current) {
            const containerRect = imageContainerRef.current.getBoundingClientRect();
            // Используем центр экрана для увеличения
            const clickX = containerRect.width / 2;
            const clickY = containerRect.height / 2;
            
            // Вычисляем начальную позицию для центрирования
            const initialTranslate = {
              x: 0,
              y: 0
            };
            
            setImageScales(prev => {
              const newMap = new Map(prev);
              newMap.set(currentIndex, targetScale);
              return newMap;
            });
            setImageTranslates(prev => {
              const newMap = new Map(prev);
              newMap.set(currentIndex, initialTranslate);
              return newMap;
            });
          }
          setLastTapTime(0);
        }
        setTouchStart(null);
        setTouchEnd(null);
        setTouchStartY(null);
        setTouchEndY(null);
        setIsVerticalSwipe(false);
        setIsPanning(false);
        setPanStartTranslate(null);
        setPanStartScale(null);
        setPanImagePoint(null);
        setPanContainerCenter(null);
        return;
      }
      
      if (distance < 10) {
        setLastTapTime(currentTime);
      }
    }
    
    // Обычный свайп одним пальцем (только если изображение не увеличено)
    if (!touchStart || touchEnd === null || !touchStartY || touchEndY === null) {
      // Плавно возвращаем на место
      setIsTransitioning(true);
      setSwipeOffset(0);
      setSwipeOffsetY(0);
      setIsVerticalSwipe(false);
      setTimeout(() => setIsTransitioning(false), 300);
      setTouchStart(null);
      setTouchEnd(null);
      setTouchStartY(null);
      setTouchEndY(null);
      setIsPanning(false);
      setPanStartTranslate(null);
      setPanStartScale(null);
      setPanImagePoint(null);
      setPanContainerCenter(null);
      return;
    }

    const distanceX = touchStart - touchEnd;
    const distanceY = touchStartY - touchEndY;
    const absX = Math.abs(distanceX);
    const absY = Math.abs(distanceY);
    
    // Проверяем, что изображение не увеличено (иначе не обрабатываем свайп)
    const currentScale = getImageScale(currentIndex);
    if (currentScale > MIN_SCALE) {
      // Если изображение увеличено, просто сбрасываем свайп
      setIsTransitioning(true);
      setSwipeOffset(0);
      setSwipeOffsetY(0);
      setTimeout(() => setIsTransitioning(false), 300);
      setTouchStart(null);
      setTouchEnd(null);
      setTouchStartY(null);
      setTouchEndY(null);
      setIsVerticalSwipe(false);
      setIsPanning(false);
      setPanStartTranslate(null);
      setPanStartScale(null);
      setPanImagePoint(null);
      setPanContainerCenter(null);
      return;
    }

    // Если это вертикальный свайп
    if (isVerticalSwipe || absY >= absX) {
      // Свайп вверх для закрытия
      if (distanceY > minSwipeDistance) {
        // Если свайп был достаточно сильным, продолжаем анимацию закрытия от текущей позиции
        const currentImage = imageRefs.current[currentIndex];
        if (currentImage && imageContainerRef.current && swipeOffsetY < -minSwipeDistance) {
          // Используем текущее смещение для плавной анимации
          handleCloseWithAnimation();
        } else {
          handleCloseWithAnimation();
        }
      } else {
        // Если свайп недостаточно сильный, плавно возвращаем на место
        setIsTransitioning(true);
        setSwipeOffsetY(0);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 300);
      }
    } else {
      // Горизонтальный свайп для навигации
      const isLeftSwipe = distanceX > minSwipeDistance;
      const isRightSwipe = distanceX < -minSwipeDistance;

      if (isLeftSwipe) {
        handleNext();
      } else if (isRightSwipe) {
        handlePrevious();
      } else {
        // Если свайп недостаточно сильный, плавно возвращаем на место
        setIsTransitioning(true);
        setSwipeOffset(0);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 300);
      }
    }
    
    setTouchStart(null);
    setTouchEnd(null);
    setTouchStartY(null);
    setTouchEndY(null);
    setIsVerticalSwipe(false);
  };

  if (images.length === 0) return null;

  // Рендерим модальное окно через portal в body, чтобы избежать проблем с позиционированием
  const modalContent = (
    <div 
      className={styles.imageViewer} 
      onClick={handleOverlayClick} 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        background: (() => {
          if (isAnimating) return 'rgba(0, 0, 0, 0)';
          if (swipeOffsetY < 0) {
            // При свайпе вверх уменьшаем прозрачность фона
            const opacity = Math.max(0, 0.95 - Math.abs(swipeOffsetY) / 400);
            return `rgba(0, 0, 0, ${opacity})`;
          }
          return 'rgba(0, 0, 0, 0.95)';
        })(),
        // Transition включается только когда shouldTransitionBackground = true и нет активного свайпа
        transition: shouldTransitionBackground && swipeOffsetY === 0 ? 'background 0.3s ease' : 'none'
      }}
    >
      <div 
        ref={imageContainerRef}
        className={styles.imageContainer} 
        onClick={handleImageClick}
        style={{
          opacity: showSliderContainer ? 1 : 0,
          transition: 'opacity 0.15s ease'
        }}
      >
        <div 
          className={styles.imagesSlider}
          style={{
            // Контейнер двигается влево на (currentIndex * 100% ширины контейнера) минус swipeOffset
            transform: `translateX(calc(-${currentIndex * 100}% + ${swipeOffset}px)) translateY(${swipeOffsetY}px)`,
            transition: isTransitioning ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
            opacity: swipeOffsetY < 0 ? Math.max(0, 1 - Math.abs(swipeOffsetY) / 300) : 1
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {images.map((image, index) => {
            const scale = getImageScale(index);
            const translate = getImageTranslate(index);
            
            const slideStyle: React.CSSProperties = {};
            
            const imageStyle: React.CSSProperties = {
              ...(index === initialIndex && index === currentIndex && isAnimating && !hasAnimated ? {
                opacity: 0
              } : {}),
              transform: scale > 1 || translate.x !== 0 || translate.y !== 0
                ? `translate(${translate.x}px, ${translate.y}px) scale(${scale})`
                : undefined,
              // Отключаем transition при активном zoom или pan для мгновенного отклика
              // При возврате к границам используем пружинистый эффект с легким отскоком
              transition: (isZooming || isPanning) ? 'none' : 'transform 0.35s cubic-bezier(0.34, 1.45, 0.64, 1)',
              transformOrigin: 'center center'
            };
            
            return (
              <div 
                key={index}
                className={styles.imageSlide}
                style={slideStyle}
              >
                <div 
                  className={styles.imageWrapper} 
                  style={imageStyle}
                  ref={(el) => {
                    imageWrapperRefs.current[index] = el;
                  }}
                >
                  {imagesThumb?.[index] && imagesThumb[index] !== image ? (
                    <>
                      <img 
                        src={imagesThumb[index]} 
                        alt=""
                        className={`${styles.fullImage} ${styles.imageThumb}`}
                        aria-hidden
                      />
                      <img 
                        ref={(el) => {
                          imageRefs.current[index] = el;
                          if (el) {
                            el.onload = () => {
                              setFullLoadedIndices(prev => new Set(Array.from(prev).concat(index)));
                              const wrapper = imageWrapperRefs.current[index];
                              if (wrapper) saveBaseImageSize(index, el, wrapper);
                            };
                            if (el.complete && el.naturalWidth > 0) {
                              requestAnimationFrame(() => {
                                setFullLoadedIndices(prev => prev.has(index) ? prev : new Set(Array.from(prev).concat(index)));
                              });
                            }
                          }
                        }}
                        src={image} 
                        alt={`Photo ${index + 1} of ${images.length}`}
                        className={`${styles.fullImage} ${styles.imageFullProgressive} ${fullLoadedIndices.has(index) ? styles.imageFullProgressiveVisible : ''}`}
                        onLoad={(e) => {
                          setFullLoadedIndices(prev => prev.has(index) ? prev : new Set(Array.from(prev).concat(index)));
                          const target = e.target as HTMLImageElement;
                          const wrapper = imageWrapperRefs.current[index];
                          if (wrapper) saveBaseImageSize(index, target, wrapper);
                        }}
                      />
                    </>
                  ) : (
                    <img 
                      ref={(el) => {
                        imageRefs.current[index] = el;
                        if (el) {
                          el.onload = () => {
                            const wrapper = imageWrapperRefs.current[index];
                            if (wrapper) saveBaseImageSize(index, el, wrapper);
                          };
                          if (el.complete) {
                            const wrapper = imageWrapperRefs.current[index];
                            if (wrapper) saveBaseImageSize(index, el, wrapper);
                          }
                        }
                      }}
                      src={image} 
                      alt={`Photo ${index + 1} of ${images.length}`}
                      className={styles.fullImage}
                    />
                  )}
                  {/* Кнопка участников - в углу фото, поверх изображения (только для текущего слайда) */}
                  {siteId && onShowParticipants && index === currentIndex && (
                    <button
                      className={styles.participantsButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowParticipants();
                      }}
                      title="Участники объекта"
                    >
                      <i className="ri-group-line"></i>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {images.length > 1 && (
        <div 
          className={styles.imageCounter}
          onClick={(e) => e.stopPropagation()}
        >
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {images.length > 1 && (
        <div 
          className={styles.thumbnails}
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((image, index) => (
            <img
              key={index}
              src={(imagesThumb?.[index] && imagesThumb[index] !== image) ? imagesThumb[index] : image}
              alt={`Thumbnail ${index + 1}`}
              className={`${styles.thumbnail} ${index === currentIndex ? styles.active : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!isTransitioning) {
                  setIsTransitioning(true);
                  setCurrentIndex(index);
                  setTimeout(() => setIsTransitioning(false), 300);
                }
              }}
            />
          ))}
        </div>
      )}

      {showActions && actions.length > 0 && (
        <div 
          className={styles.actionsPanel}
          onClick={(e) => e.stopPropagation()}
        >
          {actions.map((action, index) => (
            <button
              key={index}
              className={`${styles.actionButton} ${styles[`actionButton${action.variant || 'default'}`]}`}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              title={action.label}
            >
              <i className={action.icon}></i>
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Используем portal для рендеринга в body, чтобы избежать проблем с позиционированием
  return typeof document !== 'undefined' 
    ? createPortal(modalContent, document.body)
    : null;
};

export default ImageViewer;
